import { useHistory } from 'react-router-dom';
import { Bell, AlertCircle, CalendarClock } from 'lucide-react';
import { useNotificationsQuery } from '@features/notifications/hooks/useNotificationsQuery.js';
import { cn } from '@utils/Cn.js';

export default function NotificationList({ onNavigate }: { onNavigate?: () => void }) {
  const history = useHistory();
  const { data: notifications } = useNotificationsQuery();

  const count = notifications?.totalCount ?? 0;

  function handleNavigate(path: string) {
    onNavigate?.();
    history.push(path);
  }

  if (!notifications) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate border-t-transparent" />
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="flex flex-col items-center gap-1.5 py-8 text-center">
        <Bell size={18} className="text-ink-faint/50" />
        <p className="text-[11px] font-semibold text-ink-faint">No upcoming reminders</p>
        <p className="text-[9px] text-ink-faint/60 max-w-52">All policies and enquiries are up to date.</p>
      </div>
    );
  }

  return (
    <div className="py-1.5">
      <p className="px-4 py-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-ink-faint">
        Upcoming Reminders &middot; {count}
      </p>
      {notifications.items.map((item) => (
        <button
          key={`${item.type}-${item.id}`}
          onClick={() => {
            handleNavigate(
              item.type === 'policy_renewal'
                ? `/policies/${item.id}`
                : `/enquiries/${item.id}`,
            );
          }}
          className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-paper/70 active:bg-paper cursor-pointer border-0"
        >
          <div
            className={cn(
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
              item.type === 'policy_renewal'
                ? item.daysLeft <= 0
                  ? 'bg-red-bg text-red-fg'
                  : item.daysLeft <= 7
                    ? 'bg-amber-bg text-amber-fg'
                    : 'bg-green-bg text-green-fg'
                : 'bg-slate/10 text-slate',
            )}
          >
            {item.type === 'policy_renewal' ? (
              <AlertCircle size={13} />
            ) : (
              <CalendarClock size={13} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-ink leading-snug truncate">
              {item.type === 'policy_renewal' ? item.clientName : item.name}
            </p>
            <p className="text-[10px] text-ink-faint leading-snug truncate mt-0.5">
              {item.type === 'policy_renewal'
                ? `${item.policyType} policy`
                : `${item.policyType} enquiry`}
            </p>
            <p
              className={cn(
                'text-[9px] font-semibold leading-snug mt-0.5',
                item.daysLeft <= 0
                  ? 'text-red-fg'
                  : item.daysLeft <= 7
                    ? 'text-amber-fg'
                    : 'text-green-fg',
              )}
            >
              {item.daysLeft <= 0
                ? 'Overdue'
                : item.daysLeft === 1
                  ? 'Due tomorrow'
                  : `Due in ${String(item.daysLeft)} days`}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}
